import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Sidebar from "../../commons/Sidebar/Sidebar";
import Header from "../../commons/Header/Header";
import "notosans-fontface/css/notosans-fontface.css";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { UI_CONSTANTS } from "../../utils/constants";

const USER_TYPE_MAP = {
  driver: "DRIVER",
  customer: "CUSTOMER",
  restaurant: "RESTAURANT",
};

const AUDIENCE_TABS = [
  { key: "driver", label: "Driver" },
  { key: "customer", label: "Customer" },
  { key: "restaurant", label: "Restaurant" },
];

const normalizeOptions = (type, list = []) => {
  if (!Array.isArray(list)) return [];

  if (type === "customer") {
    return list
      .map((item) => ({
        id: item?._id,
        name: item?.full_name || "N/A",
      }))
      .filter((item) => item.id);
  }

  if (type === "restaurant") {
    return list
      .map((item) => ({
        id: item?.restaurant_id?._id || item?._id,
        name: item?.restaurant_id?.restaurant_name || item?.restaurant_name || "N/A",
      }))
      .filter((item) => item.id);
  }

  if (type === "driver") {
    return list
      .map((item) => ({
        id: item?.driver?._id || item?.driver_id?._id || item?._id,
        name: item?.driver?.full_name || item?.full_name || "N/A",
      }))
      .filter((item) => item.id);
  }

  return [];
};

const NAME_MAX_LENGTH = 150;

const formatOptionName = (name = "") => {
  const text = String(name || "N/A").trim();
  if (text.length <= NAME_MAX_LENGTH) return text;
  return `${text.slice(0, NAME_MAX_LENGTH)}...`;
};

const UserCheckboxDropdown = ({
  label,
  placeholder,
  options,
  selectedIds,
  onChange,
  loading = false,
  disabled = false,
  width,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const allSelected = options.length > 0 && selectedIds.length === options.length;

  const toggleOption = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map((option) => option.id));
    }
  };

  const triggerLabel = loading
    ? "Loading..."
    : selectedIds.length > 0
      ? `${selectedIds.length} selected`
      : placeholder;

  const widthStyle = width ? { width: typeof width === "number" ? `${width}px` : width } : undefined;

  return (
    <div className="form-group mt-4 notification-user-dropdown" style={widthStyle}>
      <label className="text-white">{label}</label>
      <div className={`dropdown ${open ? "show" : ""}`} ref={dropdownRef} style={widthStyle}>
        <button
          type="button"
          className="form-control text-left d-flex align-items-center justify-content-between notification-user-dropdown__trigger"
          disabled={disabled || loading}
          onClick={() => setOpen((prev) => !prev)}
          style={widthStyle}
        >
          <span>{triggerLabel}</span>
          <span aria-hidden="true">▾</span>
        </button>

        {open && (
          <div
            className="dropdown-menu show p-2 shadow notification-user-dropdown__menu"
            style={widthStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-1 pb-2 mb-2 border-bottom">
              <button
                type="button"
                className="btn btn-sm btn-primary w-100"
                onClick={handleSelectAll}
                disabled={options.length === 0}
              >
                {allSelected ? "Deselect All" : "Select All"}
              </button>
            </div>

            {options.length === 0 ? (
              <div className="px-2 py-2 notification-user-dropdown__empty">No users found</div>
            ) : (
              options.map((option) => {
                const checked = selectedIds.includes(option.id);
                return (
                  <label
                    key={option.id}
                    className="px-2 py-2 mb-0 notification-user-dropdown__option"
                  >
                    <input
                      type="checkbox"
                      className="d-none"
                      checked={checked}
                      onChange={() => toggleOption(option.id)}
                    />
                    <span className="checkbox" />
                    <span className="notification-user-dropdown__name" title={option.name}>
                      {formatOptionName(option.name)}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function AddNotification() {
  const navigate = useNavigate();
  const tabsRef = useRef(null);
  const [cms, setCms] = useState({ type: "driver" });
  const [tabsWidth, setTabsWidth] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return undefined;

    const syncWidth = () => {
      setTabsWidth(Math.ceil(el.getBoundingClientRect().width));
    };

    syncWidth();
    const observer = new ResizeObserver(syncWidth);
    observer.observe(el);
    window.addEventListener("resize", syncWidth);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncWidth);
    };
  }, []);

  const getCustomerList = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const response = await handleApiCall("get", endpoints.customerListDropdown);
      if (response?.data?.code === 200) {
        setCustomers(response?.data?.data?.data || []);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  const getRestaurantList = useCallback(async () => {
    setLoadingRestaurants(true);
    try {
      const response = await handleApiCall("get", endpoints.restaurantListDropdown);
      if (response?.data?.code === 200) {
        const restaurantData = response?.data?.data;
        setRestaurants(Array.isArray(restaurantData) ? restaurantData : restaurantData?.data || []);
      } else {
        setRestaurants([]);
      }
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      setRestaurants([]);
    } finally {
      setLoadingRestaurants(false);
    }
  }, []);

  const getDriverList = useCallback(async () => {
    setLoadingDrivers(true);
    try {
      const response = await handleApiCall(
        "get",
        `${endpoints.getDriverList}?page=1&limit=1000`
      );
      if (response?.data?.code === 200) {
        setDrivers(response?.data?.data?.result || []);
      } else {
        setDrivers([]);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setDrivers([]);
    } finally {
      setLoadingDrivers(false);
    }
  }, []);

  useEffect(() => {
    setTitle("");
    setDescription("");
    setSelectedUserIds([]);

    if (cms.type === "customer") getCustomerList();
    if (cms.type === "restaurant") getRestaurantList();
    if (cms.type === "driver") getDriverList();
  }, [cms.type, getCustomerList, getRestaurantList, getDriverList]);

  const dropdownOptions = useMemo(() => {
    if (cms.type === "customer") return normalizeOptions("customer", customers);
    if (cms.type === "restaurant") return normalizeOptions("restaurant", restaurants);
    if (cms.type === "driver") return normalizeOptions("driver", drivers);
    return [];
  }, [cms.type, customers, restaurants, drivers]);

  const handleSendNotification = async () => {
    if (!title.trim()) {
      toast.error("Please enter notification title");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter notification description");
      return;
    }

    if (selectedUserIds.length === 0) {
      toast.error(`Please select at least one ${cms.type}`);
      return;
    }

    setSending(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        user_type: USER_TYPE_MAP[cms.type],
        users: selectedUserIds,
      };

      const response = await handleApiCall("post", endpoints.addNotification, payload);
      if (response?.data?.code === 200 || response?.data?.code === 201) {
        toast.success(response?.data?.message || "Notification sent successfully");
        navigate(UI_CONSTANTS.ROUTES.NOTIFICATION_LIST);
        return;
      }
    } catch (error) {
      console.error("Error sending notification:", error);
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    if (sending) return;
    navigate(UI_CONSTANTS.ROUTES.NOTIFICATION_LIST);
  };

  const handleCancel = () => {
    if (sending) return;
    setTitle("");
    setDescription("");
    setSelectedUserIds([]);
    navigate(UI_CONSTANTS.ROUTES.NOTIFICATION_LIST);
  };

  const renderCommonFields = () => (
    <>
      <div className="form-group mt-4">
        <label className="text-white">Title</label>
        <input
          type="text"
          className="form-control"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter notification title"
        />
      </div>
      <div className="form-group">
        <label className="text-white">Description</label>
        <textarea
          className="form-control rounded-2 py-3"
          rows="4"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter notification description"
        />
      </div>
      <div>
        <button className="btn btn-secondary" onClick={handleCancel} disabled={sending}>
          Cancel
        </button>
        <button className="btn btn-primary ml-4" onClick={handleSendNotification} disabled={sending}>
          {sending ? "Sending..." : "Send"}
        </button>
      </div>
    </>
  );

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar status={true} />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Add Notification</h3>
            </div>
            <div className="mb-3">
              <button
                type="button"
                className="btn btn-default rounded-lg"
                onClick={handleBack}
                disabled={sending}
              >
                Back
              </button>
            </div>
            <div className="row mx-0 main-content-body">
              <div className="col-md-12 mb-26 px-0 mt-2">
                <ul
                  className="nav common-tabs cms-tabs notification-audience-tabs"
                  id="myTab"
                  role="tablist"
                  ref={tabsRef}
                >
                  {AUDIENCE_TABS.map((tab) => (
                    <li className="nav-item" key={tab.key}>
                      <a
                        className={`nav-link br-10 w-auto text-center ${
                          cms.type === tab.key ? "active" : undefined
                        }`}
                        style={{ cursor: "pointer" }}
                        onClick={() => setCms({ type: tab.key })}
                      >
                        {tab.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="col-md-8 px-0">
                  {cms.type === "driver" && (
                    <UserCheckboxDropdown
                      label="Select Driver"
                      placeholder="Select drivers"
                      options={dropdownOptions}
                      selectedIds={selectedUserIds}
                      onChange={setSelectedUserIds}
                      loading={loadingDrivers}
                      width={tabsWidth}
                    />
                  )}

                  {cms.type === "customer" && (
                    <UserCheckboxDropdown
                      label="Select Customer"
                      placeholder="Select customers"
                      options={dropdownOptions}
                      selectedIds={selectedUserIds}
                      onChange={setSelectedUserIds}
                      loading={loadingCustomers}
                      width={tabsWidth}
                    />
                  )}

                  {cms.type === "restaurant" && (
                    <UserCheckboxDropdown
                      label="Select Restaurant"
                      placeholder="Select restaurants"
                      options={dropdownOptions}
                      selectedIds={selectedUserIds}
                      onChange={setSelectedUserIds}
                      loading={loadingRestaurants}
                      width={tabsWidth}
                    />
                  )}

                  {renderCommonFields()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddNotification;
